import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, MessageSquare, PhoneCall, Mail, RotateCcw, Save, Zap, ShieldCheck } from "lucide-react";

/**
 * /clients/:id/credentials — per-client Twilio/Vapi/Resend credential rotation.
 * When a client saves credentials here, every outbound SMS/email/call attached
 * to their bookings + leads runs under those keys instead of the global fallback.
 */
export default function ClientCredentials() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: body } = await api.get(`/clients/${id}/credentials`);
      setData(body);
    } catch (err) {
      toast.error(formatApiError(err) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (id) load(); /* eslint-disable-line */ }, [id]);

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="client-credentials-page">
      <Toaster richColors position="top-right" />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <Link to={`/clients/${id}`} className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-black mb-6" data-testid="back-to-client-link">
          <ArrowLeft className="w-4 h-4" /> back to client
        </Link>
        <div className="mb-8">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono">Per-client credentials</div>
          <h1 className="font-display text-3xl font-bold mt-1" data-testid="client-credentials-title">
            Credentials · {data?.business_name || "…"}
          </h1>
          <p className="text-sm text-neutral-600 mt-2 leading-relaxed">
            Rotate this client's Twilio, Vapi, and Resend keys so every outbound SMS/call/email fires from <strong>their</strong> account.
            Leave a provider blank to fall back to your global keys.
          </p>
        </div>

        {loading || !data ? (
          <div className="p-10 text-center text-sm text-neutral-500 border border-neutral-200 bg-white">Loading…</div>
        ) : (
          <div className="grid gap-5">
            <TwilioCard clientId={id} data={data.twilio} onSaved={load} />
            <VapiCard   clientId={id} data={data.vapi}   onSaved={load} />
            <ResendCard clientId={id} data={data.resend} onSaved={load} />
          </div>
        )}
      </div>
    </div>
  );
}


// ---- Twilio ---------------------------------------------------------------

function TwilioCard({ clientId, data, onSaved }) {
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}/credentials/twilio`, form);
      toast.success("Twilio credentials saved");
      setForm({ account_sid: "", auth_token: "", from_number: "" });
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm("Clear Twilio credentials for this client? Outbound SMS will fall back to your global Twilio.")) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${clientId}/credentials/twilio`);
      toast.success("Twilio credentials cleared — falling back to global");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Phone number required");
    setBusy(true);
    try {
      const { data: r } = await api.post(`/clients/${clientId}/credentials/twilio/test`, { to: testTo });
      toast.success(`Test SMS sent via ${r.source} · status=${r.status}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <ProviderCard
      testId="twilio-card"
      title="Twilio · SMS + voice numbers"
      icon={<MessageSquare className="w-5 h-5" />}
      configured={data.configured}
      subline={data.configured
        ? `SID ****${data.sid_last4} · From ${data.from_number}`
        : "Using global Twilio credentials as fallback."}
    >
      <div className="grid gap-3">
        <LabeledInput label="Account SID (starts with AC)" testId="twilio-sid"
                      value={form.account_sid} onChange={(v) => setForm({ ...form, account_sid: v })} placeholder="ACxxxxxxxx…" />
        <LabeledInput label="Auth token" type="password" testId="twilio-token"
                      value={form.auth_token} onChange={(v) => setForm({ ...form, auth_token: v })} placeholder="secret" />
        <LabeledInput label="From number (E.164)" testId="twilio-from"
                      value={form.from_number} onChange={(v) => setForm({ ...form, from_number: v })} placeholder="+15551234567" />
        <div className="flex gap-2 mt-2 flex-wrap">
          <button data-testid="twilio-save-btn" onClick={save} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
          {data.configured && (
            <button data-testid="twilio-clear-btn" onClick={clear} disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-sm font-semibold hover:border-rose-500 hover:text-rose-700 disabled:opacity-50">
              <RotateCcw className="w-4 h-4" /> Clear (use global)
            </button>
          )}
        </div>
      </div>
      <div className="border-t border-neutral-200 mt-5 pt-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-2">Test send</div>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="twilio-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                 placeholder="+15551234567" className="flex-1 min-w-[220px] px-3 py-2 border border-neutral-300 text-sm" />
          <button data-testid="twilio-test-btn" onClick={sendTest} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50">
            <Zap className="w-4 h-4" /> Send test SMS
          </button>
        </div>
      </div>
    </ProviderCard>
  );
}


// ---- Vapi -----------------------------------------------------------------

function VapiCard({ clientId, data, onSaved }) {
  const [form, setForm] = useState({ api_key: "", phone_number_id: "", assistant_id: "" });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}/credentials/vapi`, form);
      toast.success("Vapi credentials saved");
      setForm({ api_key: "", phone_number_id: "", assistant_id: "" });
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm("Clear Vapi credentials? Outbound AI calls will fall back to your global Vapi.")) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${clientId}/credentials/vapi`);
      toast.success("Vapi credentials cleared");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Phone number required");
    if (!window.confirm(`Place a real AI test call to ${testTo}? Vapi will charge for it.`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post(`/clients/${clientId}/credentials/vapi/test`, { to: testTo });
      toast.success(`Test call queued via ${r.source}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <ProviderCard
      testId="vapi-card"
      title="Vapi · AI voice calls"
      icon={<PhoneCall className="w-5 h-5" />}
      configured={data.configured}
      subline={data.configured
        ? `Key ****${data.key_last4}${data.phone_number_id ? ` · phone=${data.phone_number_id.slice(0, 8)}…` : ""}`
        : "Using global Vapi credentials as fallback."}
    >
      <div className="grid gap-3">
        <LabeledInput label="API key" type="password" testId="vapi-key"
                      value={form.api_key} onChange={(v) => setForm({ ...form, api_key: v })} placeholder="vapi_…" />
        <LabeledInput label="Phone number ID (optional)" testId="vapi-phone-id"
                      value={form.phone_number_id} onChange={(v) => setForm({ ...form, phone_number_id: v })} placeholder="uuid from Vapi phone-numbers list" />
        <LabeledInput label="Assistant ID (optional)" testId="vapi-assistant-id"
                      value={form.assistant_id} onChange={(v) => setForm({ ...form, assistant_id: v })} placeholder="uuid from Vapi assistants list" />
        <div className="flex gap-2 mt-2 flex-wrap">
          <button data-testid="vapi-save-btn" onClick={save} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
          {data.configured && (
            <button data-testid="vapi-clear-btn" onClick={clear} disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-sm font-semibold hover:border-rose-500 hover:text-rose-700 disabled:opacity-50">
              <RotateCcw className="w-4 h-4" /> Clear (use global)
            </button>
          )}
        </div>
      </div>
      <div className="border-t border-neutral-200 mt-5 pt-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-2">Test call (charges apply)</div>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="vapi-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                 placeholder="+15551234567" className="flex-1 min-w-[220px] px-3 py-2 border border-neutral-300 text-sm" />
          <button data-testid="vapi-test-btn" onClick={sendTest} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50">
            <Zap className="w-4 h-4" /> Place test call
          </button>
        </div>
      </div>
    </ProviderCard>
  );
}


// ---- Resend ---------------------------------------------------------------

function ResendCard({ clientId, data, onSaved }) {
  const [form, setForm] = useState({ api_key: "", from_email: "" });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${clientId}/credentials/resend`, form);
      toast.success("Resend credentials saved");
      setForm({ api_key: "", from_email: "" });
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm("Clear Resend credentials? Outbound emails will fall back to your global Resend.")) return;
    setBusy(true);
    try {
      await api.delete(`/clients/${clientId}/credentials/resend`);
      toast.success("Resend credentials cleared");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const sendTest = async () => {
    if (!testTo) return toast.error("Recipient email required");
    setBusy(true);
    try {
      const { data: r } = await api.post(`/clients/${clientId}/credentials/resend/test`, { to: testTo });
      toast.success(`Test email sent via ${r.source}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <ProviderCard
      testId="resend-card"
      title="Resend · Transactional email"
      icon={<Mail className="w-5 h-5" />}
      configured={data.configured}
      subline={data.configured
        ? `Key ****${data.key_last4} · From ${data.from_email}`
        : "Using global Resend credentials as fallback."}
    >
      <div className="grid gap-3">
        <LabeledInput label="API key (starts with re_)" type="password" testId="resend-key"
                      value={form.api_key} onChange={(v) => setForm({ ...form, api_key: v })} placeholder="re_…" />
        <LabeledInput label="From address" testId="resend-from"
                      value={form.from_email} onChange={(v) => setForm({ ...form, from_email: v })} placeholder="hello@theirdomain.com" />
        <div className="flex gap-2 mt-2 flex-wrap">
          <button data-testid="resend-save-btn" onClick={save} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-900 text-white text-sm font-semibold disabled:opacity-50">
            <Save className="w-4 h-4" /> Save
          </button>
          {data.configured && (
            <button data-testid="resend-clear-btn" onClick={clear} disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 text-sm font-semibold hover:border-rose-500 hover:text-rose-700 disabled:opacity-50">
              <RotateCcw className="w-4 h-4" /> Clear (use global)
            </button>
          )}
        </div>
      </div>
      <div className="border-t border-neutral-200 mt-5 pt-4">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono mb-2">Test send</div>
        <div className="flex gap-2 flex-wrap">
          <input data-testid="resend-test-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} type="email"
                 placeholder="you@example.com" className="flex-1 min-w-[220px] px-3 py-2 border border-neutral-300 text-sm" />
          <button data-testid="resend-test-btn" onClick={sendTest} disabled={busy}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-300 text-emerald-700 text-sm font-semibold hover:bg-emerald-50 disabled:opacity-50">
            <Zap className="w-4 h-4" /> Send test email
          </button>
        </div>
      </div>
    </ProviderCard>
  );
}


// ---- Presentational -------------------------------------------------------

function ProviderCard({ title, icon, configured, subline, children, testId }) {
  return (
    <div data-testid={testId} className="bg-white border border-neutral-200 shadow-sm">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-neutral-200">
        <div className={`w-9 h-9 flex items-center justify-center ${configured ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`}>{icon}</div>
        <div className="flex-1">
          <div className="font-display text-lg font-bold">{title}</div>
          <div className="text-[12px] text-neutral-500 mt-0.5">{subline}</div>
        </div>
        <div data-testid={`${testId}-status`} className={`text-[10px] uppercase tracking-widest font-mono px-2 py-1 border ${configured ? "border-emerald-300 text-emerald-700" : "border-neutral-300 text-neutral-500"}`}>
          {configured ? <><ShieldCheck className="w-3 h-3 inline -mt-0.5 mr-1" />client</> : "global fallback"}
        </div>
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, placeholder, type = "text", testId }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-mono mb-1.5">{label}</label>
      <input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-neutral-900" />
    </div>
  );
}
